/**
 * `<page-audit-record>` — one audit record as its own page, routed at
 * `/instances/:id/audit/:recordId`.
 *
 * The explorer opens a record in a drawer, deliberately: `04` §7.2 wants the
 * filtered list a reader assembled to survive being read. This page exists for the
 * other half of that requirement — a **link** to one record, which is what a
 * support conversation, a ticket or an email to an auditor actually contains. It
 * renders the same `<audit-record-detail>` body, so the two can never disagree
 * about what a record says.
 *
 * A record's id **is its hash**, which is what makes such a link durable: no
 * surrogate id is minted anywhere, `(instance_id, hash)` is the record contract's
 * own key (interface I8), and a hash pasted out of a JSONL export addresses the
 * same record here.
 *
 * Implements `SC-F804`'s record detail and `SC-F809`'s drill-down; the honesty
 * obligations live in `<audit-record-detail>`. Stories `SC-S804`, `SC-S805`,
 * `SC-S809`, `SC-S812`, `SC-S907`.
 */

import { LitElement, css, html, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import { toast, type BnKvCopyDetail } from "../components/index.js";

import { getAuditRecord, type AuditFilter, type AuditRecordDetail } from "../api/audit.js";
import { ApiError, parseError } from "../api/errors.js";
import { hrefFor, navigate } from "../nav.js";
import { recordDetailTemplate } from "./audit-record-detail.js";
import { auditSharedCss, errorCard, profileBadge, shortHash, whenUtc } from "./audit-shared.js";

@customElement("page-audit-record")
export class PageAuditRecord extends LitElement {
  @property() instanceId = "";
  /** The record's 64-character hash. A `rec_` prefix is tolerated server-side. */
  @property() recordId = "";

  @state() private record: AuditRecordDetail | null = null;
  @state() private loading = true;
  @state() private error: ApiError | null = null;

  static override styles = [
    auditSharedCss,
    css`
      .crumbs {
        display: flex;
        align-items: center;
        gap: var(--bino-space-xs);
        margin-bottom: var(--bino-space-sm);
        font-size: var(--bino-font-size-sm);
        color: var(--bino-text-secondary);
      }
      .crumbs a {
        color: var(--bino-primary);
        text-decoration: none;
      }
      .crumbs a:hover {
        text-decoration: underline;
      }
      .crumbs a:focus-visible {
        outline: none;
        border-radius: var(--bino-radius-sm);
        box-shadow: var(--bino-focus-ring-shadow);
      }
    `,
  ];

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  override updated(changed: PropertyValues<this>) {
    // The router reuses this element when only the id changes — a "next record"
    // link, or an in-page navigation from the pair list — so the load is keyed on
    // the property. The `!== undefined` guard skips the first update, where
    // `connectedCallback` has already started the same fetch.
    if (changed.has("recordId") && changed.get("recordId") !== undefined) void this.load();
  }

  private async load() {
    if (!this.instanceId || !this.recordId) return;
    this.loading = true;
    this.error = null;
    try {
      this.record = await getAuditRecord(this.instanceId, this.recordId);
    } catch (err) {
      this.error = parseError(err);
      this.record = null;
    } finally {
      this.loading = false;
    }
  }

  /**
   * applyFilter sends a chip's filter to the explorer.
   *
   * The record page has no list of its own to narrow, so a "search this subject"
   * chip navigates rather than filtering in place — and it carries the filter in the
   * query string, which is the same shareable spelling the explorer reads back.
   */
  private applyFilter(filter: AuditFilter) {
    const p = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) {
      if (Array.isArray(value)) for (const v of value) p.append(key, String(v));
      else if (value !== undefined) p.set(key, String(value));
    }
    navigate(`${hrefFor("instances", this.instanceId, "audit")}?${p.toString()}`);
  }

  override render() {
    const explorer = hrefFor("instances", this.instanceId, "audit");
    return html`
      <nav class="crumbs" aria-label="Breadcrumb">
        <a href=${explorer}>Audit</a><span aria-hidden="true">/</span>
        <span class="mono">${shortHash(this.recordId)}</span>
      </nav>
      <header>
        <div class="head-text">
          <h1>Audit record</h1>
          <p class="lede">
            ${this.record
              ? html`One governed query, recorded by the instance at
                  <strong>${whenUtc(this.record.ts)}</strong>. A record is addressed by its own hash,
                  so this link stays valid for as long as the record is retained.`
              : html`One governed query, addressed by its hash.`}
          </p>
        </div>
        <div class="head-actions">
          ${profileBadge(this.record?.profile)}
          <bn-button @click=${() => navigate(explorer)}>Back to the explorer</bn-button>
        </div>
      </header>

      ${this.loading
        ? html`<bn-card><bn-spinner block label="Loading the record"></bn-spinner></bn-card>`
        : this.error
          ? errorCard(
              "Could not load this record",
              this.error,
              () => void this.load(),
              html`<p class="meta">
                A record whose ingested object has been pruned under your organization's retention
                window is gone rather than faulty, and answers <code>not_found</code>. A
                <code>audit_unavailable</code> here is a control-plane condition and says nothing
                about your instance's own audit log, which remains the record of authority.
              </p>`,
            )
          : html`<bn-card
              @bn-copy=${(e: CustomEvent<BnKvCopyDetail>) =>
                toast(`${e.detail.label} copied`, "success")}
            >
              ${recordDetailTemplate(this.instanceId, this.record, (f) => this.applyFilter(f))}
            </bn-card>`}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-audit-record": PageAuditRecord;
  }
}
